// ============================================================================
// PreparingGateModal — Phase 2B-4 (Packet Auto-Preparation Before Gate)
//
// Shown when the GP clicks "Start New Referral" on a packet that is not yet
// ready. Triggers server-side preparation (classification + OCR enqueue), polls
// packet document readiness, then opens the Gate when ready / partially ready.
//
// Bounded by a ~25s UI timeout. On failure or timeout it offers a manual
// fallback — manual entry is NEVER permanently blocked. Background extraction
// may continue after the modal closes.
//
// No PHI is rendered: only document statuses/counts drive this screen.
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import { Loader2, FileSearch, ScanLine, UserCog, AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  preparePreReferralPacketForGate,
  listPacketDocuments,
  computePacketReadiness,
  PACKET_PREPARATION_STATUS,
  PACKET_READY_TO_OPEN_STATUSES,
} from '../../core/store.packets.js';

const TIMEOUT_MS = 25000; // bounded UI wait; background OCR may continue
const POLL_MS = 2500;

const PROGRESS_LINES = [
  { key: 'types', label: 'Identifying document types', Icon: FileSearch },
  { key: 'scan', label: 'Scanning documents', Icon: ScanLine },
  { key: 'patient', label: 'Preparing patient information', Icon: UserCog },
];

const PreparingGateModal = ({ packetId, onProceed, onClose }) => {
  const [phase, setPhase] = useState('preparing'); // 'preparing' | 'fallback'

  const mounted = useRef(true);
  const pollRef = useRef(null);
  const timeoutRef = useRef(null);
  const proceededRef = useRef(false);

  const cleanup = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    pollRef.current = null;
    timeoutRef.current = null;
  };

  const finishReady = () => {
    if (proceededRef.current) return;
    proceededRef.current = true;
    cleanup();
    onProceed();
  };

  const poll = async () => {
    try {
      const { documents } = await listPacketDocuments(packetId);
      if (!mounted.current) return;
      const readiness = computePacketReadiness(documents);
      if (PACKET_READY_TO_OPEN_STATUSES.includes(readiness)) {
        finishReady();
      } else if (readiness === PACKET_PREPARATION_STATUS.FAILED_BUT_MANUAL_ALLOWED) {
        cleanup();
        setPhase('fallback');
      }
      // PREPARING / NEEDS_PREPARATION → keep polling until ready or timeout.
    } catch (err) {
      // Transient read error — keep polling; the timeout offers the fallback if
      // it never recovers. Never blocks the GP permanently.
      console.error('[PreparingGateModal] poll failed:', err);
    }
  };

  const startPreparation = async () => {
    proceededRef.current = false;
    cleanup();
    setPhase('preparing');

    try {
      const res = await preparePreReferralPacketForGate(packetId);
      if (!mounted.current) return;
      if (res && PACKET_READY_TO_OPEN_STATUSES.includes(res.preparationStatus)) {
        finishReady();
        return;
      }
      if (res && res.preparationStatus === PACKET_PREPARATION_STATUS.FAILED_BUT_MANUAL_ALLOWED) {
        setPhase('fallback');
        return;
      }
    } catch (err) {
      // Prepare itself failed (e.g. provider/config error). Offer manual entry.
      console.error('[PreparingGateModal] prepare failed:', err);
      if (mounted.current) setPhase('fallback');
      return;
    }

    // Still preparing — poll readiness and arm the bounded timeout.
    pollRef.current = setInterval(poll, POLL_MS);
    timeoutRef.current = setTimeout(() => {
      if (!mounted.current) return;
      cleanup();
      setPhase('fallback');
    }, TIMEOUT_MS);
    poll();
  };

  useEffect(() => {
    mounted.current = true;
    startPreparation();
    return () => {
      mounted.current = false;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packetId]);

  const preparing = phase === 'preparing';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl border border-slate-200 overflow-hidden">
        <div className="p-6">
          <div className="flex items-center gap-3">
            {preparing ? (
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
                <Loader2 className="h-5 w-5 animate-spin" />
              </span>
            ) : (
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                <AlertCircle className="h-5 w-5" />
              </span>
            )}
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                {preparing ? 'Preparing the Gate' : 'Could not finish scanning'}
              </h2>
              <p className="text-sm text-slate-600">
                {preparing
                  ? 'Scanning packet documents and organizing patient information.'
                  : 'You can continue manually.'}
              </p>
            </div>
          </div>

          {preparing ? (
            <ul className="mt-5 space-y-3">
              {PROGRESS_LINES.map(({ key, label, Icon }) => (
                <li key={key} className="flex items-center gap-3 text-sm text-slate-700">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-slate-100 text-slate-500">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="flex-1">{label}</span>
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-500" />
                </li>
              ))}
            </ul>
          ) : (
            <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600 flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-slate-400" />
              <div>
                Manual entry is always available. You can continue and fill in patient and
                contact details yourself, or retry scanning the documents.
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-3">
          {preparing ? (
            <button
              type="button"
              onClick={() => { cleanup(); onClose(); }}
              className="px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-md text-slate-600 hover:bg-white"
            >
              Cancel
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={startPreparation}
                className="px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-md text-slate-700 hover:bg-white"
              >
                Retry scanning
              </button>
              <button
                type="button"
                onClick={finishReady}
                className="px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Continue manually
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreparingGateModal;
